import { useEffect } from 'react';

import { useUIState, useUserProfileState } from '#states/hooks.js';

/**
 * Applies the current theme and watches for system theme changes when in 'system' mode.
 * Reacts to theme changes from the user profile.
 */
export function useThemeObserver() {
  const uiState = useUIState();
  const userProfileState = useUserProfileState();

  // Get theme value - accessing it here ensures signals track the dependency
  const profile = userProfileState.profile;
  const theme = profile?.theme || 'system';
  // Also watch attributes.theme directly to catch Firestore updates
  const themeAttribute = profile?.attributes?.theme;

  useEffect(() => {
    // Apply current theme on mount and whenever the preference changes
    uiState.applyTheme(theme);

    // Only observe system changes when user preference is 'system'
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      // Re-read theme from profile in case it changed
      const currentTheme = userProfileState.profile?.theme || 'system';
      uiState.applyTheme(currentTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
    // Watch theme value and themeAttribute to react when profile loads/changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState, theme, themeAttribute]);
}

/**
 * Syncs the user's sidebar open preference from their profile to UI state.
 * When the profile loads or attributes.sidebarOpen changes, updates uiState.isSidebarOpen.
 */
export function useSidebarPreferenceObserver() {
  const uiState = useUIState();
  const userProfileState = useUserProfileState();
  const profile = userProfileState.profile;
  const sidebarAttribute = profile?.attributes?.sidebarOpen;

  useEffect(() => {
    if (sidebarAttribute !== undefined) {
      uiState.isSidebarOpen = sidebarAttribute === 'true';
    }
     
  }, [uiState, sidebarAttribute]);
}
